import {
    Box,
    Button,
    Link,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Copy } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import {
    CEMarkIcon,
    ConsultIFUIcon,
    DateOfManufactureIcon,
    ManufacturerIcon,
    MDMarkIcon,
    PrescriptionOnlyIcon,
    UDIMarkIcon,
} from '~/assets/about-page-icons';
import { APIType, Labeling } from '~/backend/types';
import {
    LabeledTextarea,
    LabeledValue,
} from '~/components/Fields/LabeledValue';
import MVisionLogo from '~/components/Logo/MVisionLogo';
import TermsOfUseDocumentLink from '~/containers/terms-of-use-modal/TermsOfUseDocumentLink';
import { useTermsOfUseDocument } from '~/hooks/documents/useTermsOfUseDocument';
import { useLabelingData } from '~/hooks/workflows/backend/useLabelingData';
import AppConfig from '~/utils/appconfig';

const WORKSPACE_PLUS_MODEL_NAME = 'Workspace+';

const copyAllToClipboard = (labelingInfo: Labeling) => {
    const textToCopy = `Manufacturer: ${labelingInfo.manufacturer}
Manufacturer Model Name: ${labelingInfo.manufacturer_model_name}
Date of Manufacture: ${labelingInfo.manufacture_date}
System Version: ${labelingInfo.system_version}
Clinical: ${labelingInfo.clinical}
UDI: ${labelingInfo.udi}`;

    navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
            enqueueSnackbar('Information copied to clipboard', {
                variant: 'success',
            });
        })
        .catch((err) => {
            enqueueSnackbar(
                `Failed to copy information to clipboard. Error: ${err}`,
                {
                    variant: 'error',
                }
            );
        });
};

export default function AboutContent({ apiType }: { apiType: APIType }) {
    const theme = useTheme();
    const { data: labelingData, isLoading } = useLabelingData(apiType);
    const { isTermsOfUseDocumentAvailable } = useTermsOfUseDocument();

    // Only show regulatory icons for Workspace+
    const showRegulatoryIcons =
        labelingData?.manufacturer_model_name === WORKSPACE_PLUS_MODEL_NAME;

    return (
        <Box
            height={'100%'}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
        >
            <Box display="grid" gap={2}>
                <Typography
                    variant="h5"
                    component="div"
                    sx={{ fontWeight: 'bold' }}
                >
                    {labelingData?.manufacturer_model_name ?? (
                        <Skeleton width={200} height={32} />
                    )}
                </Typography>

                <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr 1fr"
                    rowGap={1}
                >
                    <LabeledValue
                        label="Software Version"
                        value={labelingData?.system_version || 'dev-10.0.0'}
                        loading={isLoading}
                        testId="software-version"
                    />
                    <LabeledValue
                        label="Clinical use"
                        value={labelingData?.clinical ? 'yes' : 'no'}
                        loading={isLoading}
                    />
                    <Box>
                        <LabeledValue
                            label="Instructions for use"
                            value={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                        component="img"
                                        src={ConsultIFUIcon}
                                        alt="Instructions for Use"
                                        sx={{ ml: 0.25, height: 12 }}
                                    />
                                    <Link
                                        href={`${AppConfig.portalUrl}/documents`}
                                        target="_blank"
                                        mt={-0.25}
                                    >
                                        {`${AppConfig.portalUrl.replace(
                                            /^https?:\/\//,
                                            ''
                                        )}/documents`}
                                    </Link>
                                </Box>
                            }
                            loading={isLoading}
                        />
                    </Box>
                    <LabeledValue
                        label="UI build"
                        value={AppConfig.version}
                        testId="ui-build"
                    />
                </Box>

                <LabeledTextarea
                    label="Documents"
                    value={
                        'The electronic Instructions for Use (eIFU) are provided in Portable Document Format (PDF). To access these instructions, users require a PDF reader software application and compatible computing hardware.'
                    }
                    loading={isLoading}
                />

                {labelingData?.description && (
                    <LabeledTextarea
                        label="Description"
                        value={labelingData?.description}
                        loading={isLoading}
                    />
                )}

                {showRegulatoryIcons && (
                    <>
                        <Box display="flex" gap={4}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                    component="img"
                                    src={ManufacturerIcon}
                                    alt="Manufacturer"
                                    sx={{ height: 32 }}
                                />
                                <Typography fontSize={10} lineHeight={1.2}>
                                    MVision AI Oy,
                                    <br />
                                    Paciuksenkatu 29, 6th floor,
                                    <br />
                                    00270, Helsinki, Finland
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                    component="img"
                                    src={DateOfManufactureIcon}
                                    alt="Date of Manufacture"
                                    sx={{ height: 32 }}
                                />
                                {isLoading ? (
                                    <Skeleton
                                        variant="text"
                                        width={80}
                                        height={20}
                                    />
                                ) : (
                                    <Typography fontSize={10} component="div">
                                        {(labelingData &&
                                            labelingData.manufacture_date) ||
                                            'N/A'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1}>
                            <Box
                                component="img"
                                src={UDIMarkIcon}
                                alt="UDI"
                                sx={{
                                    height: 20,
                                }}
                            />
                            {isLoading ? (
                                <Skeleton
                                    variant="text"
                                    width={250}
                                    height={20}
                                />
                            ) : (
                                <Typography
                                    variant="caption"
                                    display="block"
                                    fontSize={10}
                                    lineHeight={1.2}
                                    data-testid="udi-value"
                                >
                                    {labelingData?.udi || 'N/A'}
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

                <Box
                    display="flex"
                    gap={4}
                    justifyContent="space-between"
                    alignItems="flex-end"
                >
                    <LabeledValue
                        label="More information"
                        value={
                            <Box fontSize={12}>
                                <Link
                                    href="mailto:info@mvision.ai"
                                    sx={{
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    info@mvision.ai
                                </Link>
                                <br />
                                <Link
                                    href="https://mvision.ai/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    www.mvision.ai
                                </Link>
                            </Box>
                        }
                    />
                    {labelingData && (
                        <Box>
                            <Button
                                startIcon={<Copy size={16} />}
                                onClick={() => copyAllToClipboard(labelingData)}
                                color="primary"
                                sx={{ px: 2 }}
                            >
                                Copy Product Information
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {AppConfig.isDemo && isTermsOfUseDocumentAvailable && (
                <Box
                    display="flex"
                    gap={4}
                    justifyContent="space-between"
                    alignItems="flex-end"
                >
                    <TermsOfUseDocumentLink
                        useTerseDescription
                        useSmallerStyles
                    />
                </Box>
            )}

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
            >
                <MVisionLogo height={25} />
                {showRegulatoryIcons && (
                    <Box display="flex" gap={2} alignItems="flex-end">
                        <Box
                            component="img"
                            src={CEMarkIcon}
                            alt="CE Mark"
                            sx={{
                                height: 25,
                            }}
                        />
                        <Box
                            component="img"
                            src={PrescriptionOnlyIcon}
                            alt="Prescription Only"
                            sx={{
                                height: 25,
                            }}
                        />
                        <Box
                            component="img"
                            src={MDMarkIcon}
                            alt="MD Mark"
                            sx={{
                                height: 25,
                            }}
                        />
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
